import type { UpgradeNight, UpgradeProbe } from "@auradata/contracts";
import {
  Button,
  Card,
  cn,
  EmptyState,
  PageHeader,
  SegmentedControl,
  StatTile,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@auradata/ui";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { useProjectNav } from "@/features/shell/useProjectNav";
import { upgradeReportQuery } from "@/lib/api/queries";

/**
 * Upgrade report (NEW-46 WP5): compares a job pinned to the next dbt line, building nightly
 * against an environment that does not contribute to lineage, with production's nearest
 * run on the current line. A deliberate visit — no polling, no prefetch from Home.
 */

export type UpgradeDays = 7 | 30 | 90;

const verdictStatus = {
  clean: "ok",
  differs: "warn",
  probe_failed: "err",
  no_baseline: "neutral",
} as const;

const verdictLabel: Record<UpgradeNight["verdict"], string> = {
  clean: "Clean",
  differs: "Differs",
  probe_failed: "Probe failed",
  no_baseline: "No baseline",
};

function seconds(ms: number | null): string {
  if (ms === null) return "—";
  const s = Math.round(ms / 1000);
  return s >= 60 ? `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s` : `${s}s`;
}

function dateLabel(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function UpgradePage({
  workspace,
  project,
  days,
  onDays,
}: {
  workspace: string;
  project: string;
  days: UpgradeDays;
  onDays: (days: UpgradeDays) => void;
}) {
  const go = useProjectNav(workspace, project);
  const { data } = useSuspenseQuery(upgradeReportQuery(workspace, project, days));

  return (
    <>
      <PageHeader
        title="Upgrade report"
        meta={
          <span className="text-fg-subtle">
            Project on {data.project.line} · dbt {data.project.version} · last {data.days} days
          </span>
        }
        actions={
          <SegmentedControl
            aria-label="Report window"
            value={String(days)}
            onValueChange={(v) => onDays(Number(v) as UpgradeDays)}
            options={[
              { value: "7", label: "7 days" },
              { value: "30", label: "30 days" },
              { value: "90", label: "90 days" },
            ]}
          />
        }
      />

      {data.probes.length === 0 ? (
        <Card>
          <EmptyState
            title="No upgrade probe yet"
            description="Add a job pinned to the next dbt line, pointed at an environment that does not contribute to lineage, and its nightly builds will be compared with production here."
            action={
              <Button variant="primary" onClick={() => go("jobs")}>
                New job
              </Button>
            }
          />
        </Card>
      ) : (
        data.probes.map((probe) => (
          <ProbeCard
            key={probe.jobId}
            probe={probe}
            onOpenJob={() => go(`jobs/${probe.jobId}`, { env: probe.environmentSlug })}
            onOpenRun={(runId) => go(`runs/${runId}`)}
          />
        ))
      )}
      {data.probes.length ? (
        <p className="text-sm text-fg-subtle">
          Nothing here is stored: each night is computed from the probe's run and the production run
          closest to it, so the report is always as fresh as the runs.
        </p>
      ) : null}
    </>
  );
}

function ProbeCard({
  probe,
  onOpenJob,
  onOpenRun,
}: {
  probe: UpgradeProbe;
  onOpenJob: () => void;
  onOpenRun: (runId: string) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <button
            type="button"
            onClick={onOpenJob}
            className="font-medium text-accent-fg hover:underline"
          >
            {probe.jobName}
          </button>
          <span className="text-fg-subtle">
            {probe.line} · dbt {probe.version}
          </span>
        </span>
      }
      meta={<span className="text-fg-subtle">{probe.environmentName}</span>}
    >
      <div className="flex flex-col gap-3 px-4 pt-3 pb-1">
        <p className="text-sm text-fg-muted">{probe.rollup}</p>
        <div className="flex flex-wrap gap-3">
          <StatTile label="Clean" value={probe.totals.clean} className="min-w-[110px] px-3 py-2" />
          <StatTile
            label="Differs"
            value={probe.totals.differing}
            tone={probe.totals.differing ? "warn" : "default"}
            className="min-w-[110px] px-3 py-2"
          />
          <StatTile
            label="Probe failed"
            value={probe.totals.failed}
            tone={probe.totals.failed ? "err" : "default"}
            className="min-w-[110px] px-3 py-2"
          />
          <StatTile
            label="No baseline"
            value={probe.totals.noBaseline}
            className="min-w-[110px] px-3 py-2"
          />
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40px]" />
            <TableHead className="w-[90px]">Date</TableHead>
            <TableHead className="w-[140px]">Verdict</TableHead>
            <TableHead>Summary</TableHead>
            <TableHead className="w-[90px]">Probe</TableHead>
            <TableHead className="w-[90px]">Baseline</TableHead>
            <TableHead className="w-[150px]">Duration</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {probe.nights.length === 0 ? (
            <TableEmpty colSpan={7}>No nights in this window.</TableEmpty>
          ) : null}
          {probe.nights.map((night) => {
            const open = expanded === night.probe.id;
            return (
              <NightRow
                key={night.probe.id}
                night={night}
                open={open}
                onToggle={() => setExpanded(open ? null : night.probe.id)}
                onOpenRun={onOpenRun}
              />
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

function NightRow({
  night,
  open,
  onToggle,
  onOpenRun,
}: {
  night: UpgradeNight;
  open: boolean;
  onToggle: () => void;
  onOpenRun: (runId: string) => void;
}) {
  return (
    <>
      <TableRow>
        <TableCell>
          <button
            type="button"
            aria-expanded={open}
            aria-label={`${open ? "Collapse" : "Expand"} ${dateLabel(night.probe.startedAt)}`}
            onClick={onToggle}
            className="flex size-6 items-center justify-center rounded-xs text-fg-subtle hover:bg-hover hover:text-fg"
          >
            <ChevronRight className={cn("size-3.5 transition-transform", open && "rotate-90")} />
          </button>
        </TableCell>
        <TableCell mono muted>
          {dateLabel(night.probe.startedAt)}
        </TableCell>
        <TableCell>
          <StatusBadge status={verdictStatus[night.verdict]}>
            {verdictLabel[night.verdict]}
          </StatusBadge>
        </TableCell>
        <TableCell className="text-fg-muted">{night.summary}</TableCell>
        <TableCell mono>
          <button
            type="button"
            onClick={() => onOpenRun(night.probe.id)}
            className="text-accent-fg hover:underline"
          >
            #{night.probe.number}
          </button>
        </TableCell>
        <TableCell mono>
          {night.baseline ? (
            <button
              type="button"
              onClick={() => onOpenRun((night.baseline as NonNullable<typeof night.baseline>).id)}
              className="text-accent-fg hover:underline"
            >
              #{night.baseline.number}
            </button>
          ) : (
            "—"
          )}
        </TableCell>
        <TableCell mono muted>
          {seconds(night.probe.durationMs)} vs {seconds(night.baseline?.durationMs ?? null)}
        </TableCell>
      </TableRow>
      {open ? (
        <TableRow>
          <TableCell colSpan={7} className="bg-surface-subtle">
            <div className="grid grid-cols-2 gap-4 px-2 py-3 text-sm">
              <NightList title="Failed only on 1.12" items={night.modelsFailedOnlyOnProbe} />
              <NightList title="Failed only on 1.11" items={night.modelsFailedOnlyOnBaseline} />
              <div>
                <h3 className="mb-1 font-medium text-fg">Tests that changed</h3>
                {night.testsChanged.length === 0 ? (
                  <p className="text-fg-subtle">None</p>
                ) : (
                  <ul className="flex flex-col gap-0.5">
                    {night.testsChanged.map((t) => (
                      <li key={t.name} className="font-mono text-xs text-fg-muted">
                        {t.name} · {t.baseline} → {t.probe}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h3 className="mb-1 font-medium text-fg">New deprecation warnings</h3>
                {night.deprecations.length === 0 ? (
                  <p className="text-fg-subtle">None</p>
                ) : (
                  <ul className="flex flex-col gap-0.5">
                    {night.deprecations.map((d) => (
                      <li key={d.message} className="text-xs text-fg-muted">
                        {d.message} · ×{d.count}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

function NightList({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <h3 className="mb-1 font-medium text-fg">{title}</h3>
      {items.length === 0 ? (
        <p className="text-fg-subtle">None</p>
      ) : (
        <ul className="flex flex-col gap-0.5">
          {items.map((m) => (
            <li key={m} className="font-mono text-xs text-fg-muted">
              {m}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
